// The shell's dialogs (slice 05): one modal frame, and the few small forms
// the menus open. Every dialog is a real dialog — labelled, focus held inside,
// Escape closes — and each confirms in the words of design/07 §8 where a
// mistake would cost somebody their arrangement.
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { type CustomMenu, type Preset, type SettingsPatch, type SlotId, type UserSettings, type ViewId, SHELL_TEXT_SIZES, VIEWS } from '@dragons-table/vtt-shared';
import { TAB_ICONS, isShippedPreset, isTabbedSlot, memberLabel, roleWord } from '@dragons-table/vtt-shared';
import { ColourChooser, SLOT_LABELS, type ToolDef } from './tools';
import { coverageOf } from './model';
import { slotsOf, type ToolId } from './dockHandle';
import { useShell } from './context';
import { useTableState } from '../table/TableState';
import { BTN_MD, PRIMARY } from './ui';

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

export function Modal({ title, onClose, children, testId }: { title: string; onClose: () => void; children: ReactNode; testId?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useRef(`dlg-${Math.random().toString(36).slice(2, 8)}`).current;
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? ref.current)?.focus();
    return () => before?.focus();
  }, []);
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab' || !ref.current) return;
    const items = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0]!; const last = items[items.length - 1]!;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} data-testid={testId}
        className="max-h-full w-full max-w-md overflow-y-auto vtt-popover p-5 shadow-xl outline-none" onKeyDown={onKey}>
        <h2 id={titleId} className="vtt-title mb-3 text-lg">{title}</h2>
        {children}
      </div>
    </div>
  );
}

const btn = BTN_MD;
const primary = PRIMARY;
const danger = 'rounded bg-red-800 px-3 py-1 text-sm font-medium text-red-50 hover:bg-red-700';

// "Keep it" is for a delete, where the thing is kept; any other ask names what its cancel does ("Keep playing", "Leave it off", "Cancel").
export function ConfirmDialog({ title, body, confirmLabel, cancelLabel = 'Keep it', destructive, onConfirm, onClose }: {
  title: string; body: ReactNode; confirmLabel: string; cancelLabel?: string; destructive?: boolean; onConfirm: () => void; onClose: () => void;
}) {
  return (
    <Modal title={title} onClose={onClose} testId="confirm">
      <div className="space-y-4 text-sm text-stone-300">
        <div>{body}</div>
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>{cancelLabel}</button>
          <button type="button" className={destructive ? danger : primary} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </Modal>
  );
}

export function PromptDialog({ title, label, initial = '', submitLabel, note, maxLength = 40, onSubmit, onClose }: {
  title: string; label: string; initial?: string; submitLabel: string; note?: ReactNode; maxLength?: number;
  onSubmit: (value: string) => string | null; onClose: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(onSubmit(value));
  };
  return (
    <Modal title={title} onClose={onClose} testId="prompt">
      <form className="space-y-3 text-sm" onSubmit={submit}>
        <label className="block">
          <span className="text-stone-300">{label}</span>
          <input className="mt-1 w-full rounded bg-stone-800 px-2 py-1" value={value} maxLength={maxLength} onChange={(e) => setValue(e.target.value)} />
        </label>
        {note && <p className="text-xs text-stone-500">{note}</p>}
        {error && <p role="alert" className="text-amber-300">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>Cancel</button>
          <button type="submit" className={primary}>{submitLabel}</button>
        </div>
      </form>
    </Modal>
  );
}

/** A tool, chosen from a menu: where to put it, whether to take it off, which drop-downs hold it. */
export function ToolDialog({ tool, view, placed, menus, onPlace, onRemove, onToggleMenu, onNewMenu, onClose }: {
  tool: ToolDef; view: ViewId; placed: SlotId | null; menus: CustomMenu[];
  onPlace: (slot: SlotId) => void; onRemove: () => void; onToggleMenu: (menuId: string, add: boolean) => void; onNewMenu: () => void; onClose: () => void;
}) {
  const slots = slotsOf(view).filter((s) => tool.slots.includes(s));
  return (
    <Modal title={tool.title} onClose={onClose} testId="tool-dialog">
      <div className="space-y-4 text-sm">
        <p className="text-stone-400">{tool.blurb}</p>
        <div>
          <p className="mb-1 text-stone-300">Show it on</p>
          <div className="flex flex-wrap gap-2">
            {slots.map((slot) => (
              <button key={slot} type="button" className={btn} disabled={placed === slot} onClick={() => onPlace(slot)}>
                {SLOT_LABELS[slot]}{placed === slot ? ' (here now)' : ''}
              </button>
            ))}
            {slots.length === 0 && <span className="text-stone-500">This tool cannot sit in this window's bars.</span>}
          </div>
          {placed && (
            <button type="button" className={`${btn} mt-2`} onClick={onRemove}>Remove from the {SLOT_LABELS[placed].toLowerCase()}</button>
          )}
        </div>
        <div>
          <p className="mb-1 text-stone-300">Your drop-downs</p>
          <div className="flex flex-wrap gap-2">
            {menus.map((m) => {
              const held = m.tools.includes(tool.id);
              return (
                <button key={m.id} type="button" className={btn} aria-pressed={held} onClick={() => onToggleMenu(m.id, !held)}>
                  {held ? `Remove from ${m.name}` : `Add to ${m.name}`}
                </button>
              );
            })}
            <button type="button" className={btn} onClick={onNewMenu}>Add to a new drop-down…</button>
          </div>
        </div>
        <div className="flex justify-end">
          <button type="button" className={btn} onClick={onClose}>Close</button>
        </div>
      </div>
    </Modal>
  );
}

/** The `+` on a bar: what may go here. */
export function AddToSlotDialog({ slot, tools, held, onPick, onClose }: {
  slot: SlotId; tools: ToolDef[]; held: readonly ToolId[]; onPick: (tool: ToolId) => void; onClose: () => void;
}) {
  const fitting = tools.filter((t) => t.slots.includes(slot));
  const current = held.length > 0;
  return (
    <Modal title={`Add to the ${SLOT_LABELS[slot].toLowerCase()}`} onClose={onClose} testId="add-dialog">
      <div className="space-y-3 text-sm">
        {current && (isTabbedSlot(slot)
          ? <p className="text-stone-400">Another tool joins this bar as a new tab beside what is there.</p>
          : <p className="text-stone-400">This bar holds one thing; choosing another replaces it. Nothing is lost — every tool stays in the top bar's menus.</p>)}
        <ul className="divide-y divide-stone-800">
          {fitting.map((t) => (
            <li key={t.id} className="py-2">
              <button type="button" className="w-full text-left hover:bg-stone-800 rounded px-2 py-1 disabled:opacity-50" disabled={held.includes(t.id)} onClick={() => onPick(t.id)}>
                <span className="font-medium">{t.title}</span>{held.includes(t.id) ? ' — here now' : ''}
                <span className="block text-xs text-stone-500">{t.blurb}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="flex justify-end"><button type="button" className={btn} onClick={onClose}>Close</button></div>
      </div>
    </Modal>
  );
}

/**
 * A tab's menu (qa-runs#404): move it a place left or right in its bar, or to the other side bar — no dragging — pick
 * an icon from the built-in set to show instead of its name (or put the name back), or close it. Reached from the bar's
 * ⋮ for the showing tab, and from a right-click, a long press or the context-menu key on any tab.
 */
export function TabMenuDialog({ tool, slot, index, count, icon, onMove, onIcon, onClose: onCloseTab, onDismiss }: {
  tool: ToolDef; slot: SlotId; index: number; count: number; icon: string | null;
  onMove: (where: 'left' | 'right' | 'other') => void; onIcon: (icon: string | null) => void; onClose: () => void; onDismiss: () => void;
}) {
  const side = isTabbedSlot(slot);
  const other = slot === 'left' ? 'right' : 'left';
  return (
    <Modal title={`${tool.title} tab`} onClose={onDismiss} testId="tab-menu">
      <div className="space-y-4 text-sm">
        {side && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className={btn} disabled={index <= 0} onClick={() => onMove('left')}>Move left</button>
            <button type="button" className={btn} disabled={index >= count - 1} onClick={() => onMove('right')}>Move right</button>
            <button type="button" className={btn} onClick={() => onMove('other')}>Move to the {other} bar</button>
          </div>
        )}
        <fieldset>
          <legend className="mb-1 text-stone-300">Show on its tab</legend>
          <div className="flex flex-wrap gap-1" role="group" aria-label="Tab icon">
            <button type="button" className={btn} aria-pressed={icon === null} onClick={() => onIcon(null)}>Its name</button>
            {TAB_ICONS.map((i) => (
              <button key={i.id} type="button" className={`${btn} min-w-11 text-base`} aria-pressed={icon === i.id} aria-label={i.name} title={i.name} onClick={() => onIcon(i.id)}>
                {i.glyph}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-stone-500">The icon follows you to every table of this system.</p>
        </fieldset>
        <div className="flex justify-between gap-2">
          <button type="button" className={btn} onClick={onCloseTab}>Close the tab</button>
          <button type="button" className={btn} onClick={onDismiss}>Done</button>
        </div>
      </div>
    </Modal>
  );
}

export function SavePresetDialog({ view, names, onSave, onClose }: {
  view: ViewId; names: string[]; onSave: (name: string, views: ViewId[]) => string | null; onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [views, setViews] = useState<ViewId[]>([view]);
  const [error, setError] = useState<string | null>(null);
  const replacing = names.find((n) => n.trim().toLowerCase() === name.trim().toLowerCase());
  const toggle = (v: ViewId) => setViews((vs) => (vs.includes(v) ? vs.filter((x) => x !== v) : [...vs, v]));
  return (
    <Modal title="Save as preset" onClose={onClose} testId="save-preset">
      <form className="space-y-3 text-sm" onSubmit={(e) => { e.preventDefault(); setError(onSave(name, views)); }}>
        <label className="block">
          <span className="text-stone-300">Name</span>
          <input className="mt-1 w-full rounded bg-stone-800 px-2 py-1" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
        </label>
        <fieldset>
          <legend className="text-stone-300">It covers</legend>
          {VIEWS.map((v) => (
            <label key={v} className="mr-4 inline-flex items-center gap-1">
              <input type="checkbox" checked={views.includes(v)} onChange={() => toggle(v)} /> {v === 'main' ? 'Main window' : 'Second window'}
            </label>
          ))}
          <p className="mt-1 text-xs text-stone-500">A preset changes only the windows it covers when you switch to it. The other window's arrangement is taken as it was last saved.</p>
        </fieldset>
        {replacing && <p className="text-xs text-amber-300">This replaces your preset "{replacing}".</p>}
        {error && <p role="alert" className="text-amber-300">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>Cancel</button>
          <button type="submit" className={primary} disabled={!name.trim() || views.length === 0}>Save preset</button>
        </div>
      </form>
    </Modal>
  );
}

/** Switch, rename, delete. No reset lives here (design/07 §8: never beside Save, never beside a switch). */
export function PresetsDialog({ presets, current, onSwitch, onRename, onDelete, onClose }: {
  presets: Preset[]; current: Record<ViewId, string>; onSwitch: (id: string) => void; onRename: (id: string, name: string) => string | null;
  onDelete: (id: string) => void; onClose: () => void;
}) {
  const [renaming, setRenaming] = useState<{ id: string; name: string; error: string | null } | null>(null);
  const [deleting, setDeleting] = useState<Preset | null>(null);
  const covers = (p: Preset) => { const c = coverageOf(p); return c.length === 2 ? 'both windows' : c[0] === 'main' ? 'main window' : 'second window'; };
  const inUse = (p: Preset) => VIEWS.filter((v) => current[v] === p.id);
  if (deleting) {
    return (
      <ConfirmDialog title={`Delete "${deleting.name}"?`} confirmLabel="Delete preset" destructive
        body={<p>Your windows stay as they are; only the saved preset goes.</p>}
        onConfirm={() => { onDelete(deleting.id); setDeleting(null); }} onClose={() => setDeleting(null)} />
    );
  }
  return (
    <Modal title="Presets" onClose={onClose} testId="presets">
      <ul className="divide-y divide-stone-800 text-sm" data-testid="preset-list">
        {presets.map((p) => (
          <li key={p.id} className="py-2 space-y-1" data-testid={`preset-${p.id}`}>
            {renaming?.id === p.id ? (
              <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); const err = onRename(p.id, renaming.name); if (err) setRenaming({ ...renaming, error: err }); else setRenaming(null); }}>
                <input aria-label="New name" className="rounded bg-stone-800 px-2 py-1" value={renaming.name} maxLength={40} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} />
                <button type="submit" className={primary}>Rename</button>
                <button type="button" className={btn} onClick={() => setRenaming(null)}>Cancel</button>
                {renaming.error && <span role="alert" className="text-amber-300">{renaming.error}</span>}
              </form>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{p.name}</span>
                <span className="text-xs text-stone-500">{covers(p)}{inUse(p).length ? ` · in use (${inUse(p).map((v) => (v === 'main' ? 'main' : 'second')).join(', ')})` : ''}</span>
                <span className="ml-auto flex gap-1">
                  <button type="button" className={btn} onClick={() => onSwitch(p.id)} aria-label={`Switch to ${p.name}`}>Switch</button>
                  {!isShippedPreset(p.id) && (
                    <>
                      <button type="button" className={btn} onClick={() => setRenaming({ id: p.id, name: p.name, error: null })} aria-label={`Rename ${p.name}`}>Rename</button>
                      <button type="button" className={btn} onClick={() => setDeleting(p)} aria-label={`Delete ${p.name}`}>Delete</button>
                    </>
                  )}
                </span>
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-stone-500">Switching is never automatic: the table never rearranges your screen on its own.</p>
      <div className="mt-3 flex justify-end"><button type="button" className={btn} onClick={onClose}>Close</button></div>
    </Modal>
  );
}

export function MenusDialog({ menus, onAdd, onRename, onRemove, onClose }: {
  menus: CustomMenu[]; onAdd: (name: string) => string | null; onRename: (id: string, name: string) => string | null; onRemove: (id: string) => void; onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string; error: string | null } | null>(null);
  return (
    <Modal title="Your drop-downs" onClose={onClose} testId="menus">
      <div className="space-y-3 text-sm">
        <ul className="divide-y divide-stone-800">
          {menus.length === 0 && <li className="py-2 text-stone-500">None yet. A named drop-down holds the tools you use often but not enough to keep on a bar.</li>}
          {menus.map((m) => (
            <li key={m.id} className="py-2 flex flex-wrap items-center gap-2" data-testid={`menu-row-${m.id}`}>
              {renaming?.id === m.id ? (
                <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); const err = onRename(m.id, renaming.name); if (err) setRenaming({ ...renaming, error: err }); else setRenaming(null); }}>
                  <input aria-label="New name" className="rounded bg-stone-800 px-2 py-1" value={renaming.name} maxLength={24} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} />
                  <button type="submit" className={primary}>Rename</button>
                  <button type="button" className={btn} onClick={() => setRenaming(null)}>Cancel</button>
                  {renaming.error && <span role="alert" className="text-amber-300">{renaming.error}</span>}
                </form>
              ) : (
                <>
                  <span className="font-medium">{m.name}</span>
                  <span className="text-xs text-stone-500">{m.tools.length} tool{m.tools.length === 1 ? '' : 's'}</span>
                  <span className="ml-auto flex gap-1">
                    <button type="button" className={btn} aria-label={`Rename ${m.name}`} onClick={() => setRenaming({ id: m.id, name: m.name, error: null })}>Rename</button>
                    <button type="button" className={btn} aria-label={`Delete ${m.name}`} onClick={() => onRemove(m.id)}>Delete</button>
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
        <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); const err = onAdd(name); setError(err); if (!err) setName(''); }}>
          <label className="grow">
            <span className="text-stone-300">New drop-down</span>
            <input className="mt-1 w-full rounded bg-stone-800 px-2 py-1" value={name} maxLength={24} placeholder="Combat" onChange={(e) => setName(e.target.value)} />
          </label>
          <button type="submit" className={primary} disabled={!name.trim()}>Add</button>
        </form>
        {error && <p role="alert" className="text-amber-300">{error}</p>}
        <div className="flex justify-end"><button type="button" className={btn} onClick={onClose}>Close</button></div>
      </div>
    </Modal>
  );
}

export function BaseSystemDialog({ value, system, onSet, onClose }: { value: string | null; system?: { code: string; name?: string }; onSet: (label: string | null) => void; onClose: () => void }) {
  const [label, setLabel] = useState(value ?? '');
  // A label that is the campaign's system code is that system (issue 1332): said by its name, the code kept as typed.
  const named = system?.name && label.trim() === system.code ? system.name : null;
  return (
    <Modal title="Set the base system" onClose={onClose} testId="base-system">
      <form className="space-y-3 text-sm" onSubmit={(e) => { e.preventDefault(); onSet(label.trim() || null); }}>
        <label className="block">
          <span className="text-stone-300">Base system</span>
          <input id="base-system" className="mt-1 w-full rounded bg-stone-800 px-2 py-1" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} />
        </label>
        {named && <p className="text-xs text-stone-300" data-testid="base-system-name">"{system!.code}" is {named}, this campaign's system.</p>}
        <p className="text-xs text-stone-500">A label for matching layouts: everyone's presets for this label appear at this table. It loads nothing — no rules, no content.</p>
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>Cancel</button>
          <button type="submit" className={primary}>Set</button>
        </div>
      </form>
    </Modal>
  );
}

export function ColourDialog({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="Your colour" onClose={onClose} testId="colour">
      <div className="space-y-3">
        <ColourChooser />
        <div className="flex justify-end"><button type="button" className={btn} onClick={onClose}>Close</button></div>
      </div>
    </Modal>
  );
}

/** Looking through a player's eyes (slice 09; design/11 §6): pick a person at the table, and every surface shows the table as they have it. */
export function ViewAsDialog({ onClose }: { onClose: () => void }) {
  const { table } = useShell();
  const { viewAs, viewingAs, store } = useTableState();
  // A watcher sees what a player who owns nothing sees (issue 1040): nobody to look through.
  const others = table.members.filter((m) => m.id !== table.me.id && m.role !== 'watcher');
  return (
    <Modal title="View as" onClose={onClose} testId="view-as-dialog">
      <p className="mb-3 text-sm text-stone-300">The board, the list and the properties panel exactly as that person has them — what is hidden from them is simply not there.</p>
      {others.length === 0 && <p className="text-sm text-stone-500">Nobody else is at this table yet.</p>}
      <ul className="space-y-1">
        {others.map((m) => (
          <li key={m.id}>
            <button type="button" className="w-full rounded px-2 py-1 text-left text-sm hover:bg-stone-800" aria-pressed={viewingAs?.id === m.id}
              onClick={() => { viewAs({ id: m.id, handle: m.handle, role: m.role, label: memberLabel(m, store.state.objects) }); onClose(); }}>
              {memberLabel(m, store.state.objects)} <span className="text-xs text-stone-500">{roleWord(m.role)}{m.present ? ' · here' : ''}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex justify-end gap-2">
        {viewingAs && <button type="button" className={btn} onClick={() => { viewAs(null); onClose(); }}>Back to your view</button>}
        <button type="button" className={btn} onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

/**
 * The person's text size and typeface (issue 1174): this screen's look, stored with their palette and sent to nobody.
 * Each choice is saved as it is made; the radios show what the server answered.
 */
export function TextDialog({ settings, onChange, onClose }: { settings: UserSettings; onChange: (patch: SettingsPatch) => void | Promise<void>; onClose: () => void }) {
  const size = settings.textSize ?? 100;
  const face = settings.typeface ?? 'standard';
  return (
    <Modal title="Text size and typeface" onClose={onClose} testId="text-look">
      <div className="space-y-4 text-sm">
        <fieldset>
          <legend className="mb-1 font-medium">Text size</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {SHELL_TEXT_SIZES.map((n) => (
              <label key={n} className="flex items-center gap-1.5">
                <input type="radio" name="text-size" value={n} checked={size === n} onChange={() => { void onChange({ textSize: n }); }} data-testid={`text-size-${n}`} />
                {n}%
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend className="mb-1 font-medium">Typeface</legend>
          <div className="space-y-1">
            <label className="flex items-center gap-1.5">
              <input type="radio" name="typeface" value="standard" checked={face === 'standard'} onChange={() => { void onChange({ typeface: 'standard' }); }} data-testid="typeface-standard" />
              Standard
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" name="typeface" value="reading" checked={face === 'reading'} onChange={() => { void onChange({ typeface: 'reading' }); }} data-testid="typeface-reading" />
              <span style={{ fontFamily: "'OpenDyslexic', sans-serif" }}>Reading-friendly (OpenDyslexic)</span>
            </label>
          </div>
        </fieldset>
        <p className="text-xs text-stone-400">On this screen only: nobody else at the table sees your choice.</p>
        <div className="flex justify-end"><button type="button" className={btn} onClick={onClose}>Close</button></div>
      </div>
    </Modal>
  );
}
